import useSWRImmutable from "swr/immutable";
import {
    getRestAndExerciseBloodTestData
} from "../api/forms/blood-test";
import {
    BloodTestRestAndExerciseData
} from "../api/forms/blood-test/types";
import { getCPETInfo } from "../api/forms/cpet-info";
import { CPETInformation } from "../api/forms/cpet-info/types";
import { getDynamicLoops } from "../api/forms/fv-loops";
import { DynamicLoops } from "../api/forms/fv-loops/types";
import {
    getAdvancedMedicalInfo,
    getBasicMedicalInfo,
} from "../api/forms/medical-info";
import {
    AdvancedMedicalInfo,
    BasicMedicalInfo,
} from "../api/forms/medical-info/types";
import {
    getAdvancedPersonalData,
    getBasicPersonalData,
} from "../api/forms/personal-data";
import {
    AdvancedPersonalData,
    BasicPersonalData,
} from "../api/forms/personal-data/types";
import {
    getPostSpirometryData,
    getPreSpirometryData,
} from "../api/forms/spirometry";
import {
    PostSpirometryData,
    PreSpirometryData,
} from "../api/forms/spirometry/types";
import { getSymptoms } from "../api/forms/symptoms";
import { Symptoms } from "../api/forms/symptoms/types";
import { BooleanText } from "../api/types";
import useTestId from "./useTestId";

export const BOOLEAN_TEXTS = {
    [BooleanText.YES]: "Yes",
    [BooleanText.NO]: "No",
};
export const booleanOptions = Object.values(BooleanText).map((smokeStatus) => {
    return {
        id: smokeStatus,
        value: smokeStatus,
        label: BOOLEAN_TEXTS[smokeStatus],
    };
});

export enum FORM_IDS {
    PERSONAL_DATA = "PERSONAL_DATA",
    ADVANCED_PERSONAL_DATA = "ADVANCED_PERSONAL_DATA",
    MEDICAL_INFO = "MEDICAL_INFO",
    ADVANED_MEDICAL_INFO = "ADVANED_MEDICAL_INFO",
    SYMPTOMS = "SYMPTOMS",
    DYNAMIC_LOOPS = "DYNAMIC_LOOPS",
    CPET_INFO = "CPET_INFO",
    POST_SPIROMETRY = "POST_SPIROMETRY",
    PRE_SPIROMETRY = "PRE_SPIROMETRY",
    BLOOD_TEST = "BLOOD_TEST",
}

// Type mapping from FORM_IDS to data types
export type FormDataTypes = {
    [FORM_IDS.PERSONAL_DATA]: BasicPersonalData;
    [FORM_IDS.ADVANCED_PERSONAL_DATA]: AdvancedPersonalData;
    [FORM_IDS.MEDICAL_INFO]: BasicMedicalInfo;
    [FORM_IDS.ADVANED_MEDICAL_INFO]: AdvancedMedicalInfo;
    [FORM_IDS.SYMPTOMS]: Symptoms;
    [FORM_IDS.DYNAMIC_LOOPS]: DynamicLoops;
    [FORM_IDS.CPET_INFO]: CPETInformation;
    [FORM_IDS.POST_SPIROMETRY]: PostSpirometryData;
    [FORM_IDS.PRE_SPIROMETRY]: PreSpirometryData;
    [FORM_IDS.BLOOD_TEST]: BloodTestRestAndExerciseData;
};

export default function useDataInputForm<T extends FORM_IDS>({
    formId,
}: {
    formId: T;
}): {
    formData: FormDataTypes[T] | null;
    loading: boolean;
    error: Error | undefined;
    setFromData: (data: FormDataTypes[T]) => Promise<void>;
} {
    const testId = useTestId();

    // Create SWR key - only fetch if testId exists
    const swrKey = testId ? ["data-input-form", formId, testId] : null;

    // SWR fetcher function
    const fetcher = async ([, formId, testId]: [string, T, string]) => {
        if (!testId) {
            console.error("Test ID is required");
            return null;
        }

        switch (formId) {
            case FORM_IDS.PERSONAL_DATA:
                return await getBasicPersonalData({ testId });
            case FORM_IDS.ADVANCED_PERSONAL_DATA:
                return await getAdvancedPersonalData({ testId });
            case FORM_IDS.MEDICAL_INFO:
                return await getBasicMedicalInfo({ testId });
            case FORM_IDS.ADVANED_MEDICAL_INFO:
                return await getAdvancedMedicalInfo({ testId });
            case FORM_IDS.SYMPTOMS:
                return await getSymptoms({ testId });
            case FORM_IDS.DYNAMIC_LOOPS:
                return await getDynamicLoops({ testId });
            case FORM_IDS.CPET_INFO:
                return await getCPETInfo({ testId });
            case FORM_IDS.POST_SPIROMETRY:
                return await getPostSpirometryData({ testId });
            case FORM_IDS.PRE_SPIROMETRY:
                return await getPreSpirometryData({ testId });
            case FORM_IDS.BLOOD_TEST:
                return await getRestAndExerciseBloodTestData({ testId });

            default:
                console.error("Invalid form ID");
                return null;
        }
    };

    const {
        data: formData,
        isLoading: loading,
        error,
        mutate,
    } = useSWRImmutable(swrKey, fetcher);

    return {
        formData: formData as FormDataTypes[T] | null,
        loading,
        setFromData: async (data) => {
            await mutate(data, false);
        },
        error,
    };
}
